import { useEffect, useRef, useState } from "react";
import { Flame, Upload, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MODES = [
  { id: "append", label: "Agregar", desc: "Suma el archivo tal cual a lo ya cargado" },
  { id: "replace_month", label: "Sustituir mes y año", desc: "Cambia solo el mes elegido; ignora el resto del archivo" },
  { id: "replace_all", label: "Reemplazar todo", desc: "Borra el histórico y deja solo el archivo" },
];

export function ZonasHotUploadCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const now = new Date();
  const [mode, setMode] = useState("append");
  const [ym, setYm] = useState({ year: String(now.getFullYear()), month: String(now.getMonth() + 1) });
  const refresh = () => api.get("/zonas-hot/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    if (mode === "replace_all" && !window.confirm("Se borrará todo el histórico de Zonas Hot y quedará solo lo que trae este archivo. ¿Continuar?")) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("mode", mode);
      if (mode === "replace_month") { fd.append("year", ym.year); fd.append("month", ym.month); }
      const r = await api.post("/zonas-hot/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const x = r.data;
      setMsg(mode === "append"
        ? `Agregadas ${x.rows} ventas · ${x.total} en total · ${x.with_model} con modelo`
        : mode === "replace_month"
          ? `${MONTHS[+ym.month - 1]} ${ym.year} sustituido: ${x.replaced} ventas quitadas, ${x.rows} cargadas · ${x.total} en total`
          : `Histórico reemplazado: ${x.total} ventas cargadas (antes había ${x.replaced}) · ${x.with_model} con modelo`);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-5 mb-5" data-testid="zonas-hot-upload-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ width: 36, height: 36, background: "#ffedd5", color: "#ea580c" }}>
          <Flame size={18}/>
        </div>
        <div className="flex-1">
          <h3 className="text-[15px] font-extrabold font-display">Ventas Zonas Hot</h3>
          <p className="text-[12px] text-gray-500 font-semibold mt-0.5">
            Excel &quot;Ventas Zona Hot&quot; (hoja Zona Hot). Elige cómo aplicar el archivo antes de subirlo.
            El modelo se toma de la facturación por chasis.
            {status?.rows ? ` · ${status.rows} ventas cargadas` : ""}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="zonas-hot-upload-mode">
        {MODES.map((m) => (
          <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`zonas-hot-mode-${m.id}`}
                  className={`text-left px-3 py-2 rounded-lg border transition-colors ${mode === m.id ? "border-orange-500 bg-orange-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
            <div className="text-[13px] font-extrabold">{m.label}</div>
            <div className="text-[11px] font-semibold text-gray-500">{m.desc}</div>
          </button>
        ))}
      </div>
      {mode === "replace_month" && (
        <div className="flex gap-2 mb-3 items-center text-[12px] font-bold text-gray-600" data-testid="zonas-hot-month-picker">
          Mes a sustituir:
          <select value={ym.month} onChange={(e) => setYm({ ...ym, month: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-[13px]" data-testid="zonas-hot-replace-month">
            {MONTHS.map((l, i) => <option key={l} value={String(i + 1)}>{l}</option>)}
          </select>
          <select value={ym.year} onChange={(e) => setYm({ ...ym, year: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-[13px]" data-testid="zonas-hot-replace-year">
            {Array.from({ length: 8 }, (_, i) => String(now.getFullYear() - i)).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      <form onSubmit={onUpload} className="flex gap-2 items-center flex-wrap">
        <input type="file" ref={fileRef} accept=".xlsx,.xlsm" className="text-[13px] font-semibold" data-testid="zonas-hot-file-input"/>
        <button type="submit" className="btn primary flex items-center gap-2" disabled={busy} data-testid="zonas-hot-upload-btn">
          <Upload size={14}/> {busy ? "Subiendo..." : "Subir Excel"}
        </button>
      </form>
      {msg && (
        <div className="rounded-lg p-2 mt-3 text-[12px] font-semibold flex items-center gap-2"
             style={{ background: "#dcfce7", color: "#166534" }} data-testid="zonas-hot-upload-msg">
          <CheckCircle2 size={12}/> {msg}
        </div>
      )}
      {err && <div className="rounded-lg p-2 mt-3 text-[12px] font-semibold bg-red-50 text-red-700" data-testid="zonas-hot-upload-error">{err}</div>}
    </div>
  );
}
